import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Toggle from "common/Toggle";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { createBlogTags } from "services/apiService";

interface BlogTagFormData {
  name: string;
  is_active: number;
}

interface Errors {
  name?: string;
}

const BlogTagsCreate: React.FC<{
  open: boolean;
  handleClose: () => void;
  onActionComplete: () => void;
}> = ({ open, handleClose, onActionComplete }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState<BlogTagFormData>({
    name: "",
    is_active: 0,
  });

  const [errors, setErrors] = useState<Errors>({});

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    if (errors[id as keyof Errors]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [id]: "",
      }));
    }
  };

  const handleToggleChange = (checked: number, field: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: checked ? 1 : 0,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Blog tag title is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await createBlogTags(formData);
      if (response.status_code === 200) {
        toast.success("Blog tag created successfully!");
        setFormData({ name: "", is_active: 0 });
        setErrors({});
        handleClose();
        onActionComplete();
      }
    } catch (error) {
      toast.error("Failed to create blog tag.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 0,
          padding: 3,
          boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.75rem",
          fontWeight: "bold",
          textAlign: "center",
          paddingBottom: 2,
        }}
      >
        Create Blog Tag
      </DialogTitle>
      <DialogContent sx={{ padding: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Blog Tag Title
            </Typography>
            <TextField
              variant="outlined"
              id="name"
              value={formData.name}
              onChange={handleTextFieldChange}
              placeholder="Enter tag title"
              fullWidth
              error={!!errors.name}
              helperText={errors.name || ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Typography variant="body1" fontWeight={600}>
              Status
            </Typography>
            <Toggle
              checked={formData.is_active === 1}
              onChange={(checked) => handleToggleChange(checked, "is_active")}
            />
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", paddingBottom: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          sx={{
            borderRadius: 3,
            paddingX: 3,
            fontWeight: "bold",
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.light,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          sx={{
            borderRadius: 3,
            paddingX: 3,
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          <FontAwesomeIcon icon={faCirclePlus} /> Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlogTagsCreate;
